/**
 * THE LAYOUT PICKER.
 *
 * WHY THIS EXISTS
 *   Four layouts have been registered, tested and reachable through the API for some time, and an
 *   agent could not see any of them. This is the control that makes them visible.
 *
 * IT RENDERS FROM THE SERVER, NEVER FROM A LOCAL LIST
 *   `getFormats()` reads `engine/formats.py`'s registry through `/api/amb/formats`. Keeping a copy
 *   of the four names here would be a second registry that drifts — the failure `/api/branding`
 *   already has a test guarding against. If a fifth layout lands, this picker offers it with no
 *   change to this file; if one is withdrawn, it disappears the same way.
 *
 * PARITY IS A PROPERTY OF THE SET
 *   The options are presented in registry order and styled identically. `formats.py`'s docstring
 *   is explicit that the order is presentation order and NOT a ranking, and that no layout is the
 *   unmarked member — so there is no "recommended" badge, no default pre-selection styling, and no
 *   sort by anything. Shuffle the four and no order should read as more correct.
 *
 * IT NEVER OFFERS A FILE THAT CANNOT BE MADE
 *   `docx: false` comes from the registry entry, not from a failed request. A layout that cannot
 *   be produced as a Word file says so on the option itself, before the agent clicks anything.
 *
 * SWITCHING IS FREE, AND SAYS SO
 *   The payload is frozen once the analysis has run; changing layout re-renders from it and never
 *   calls the model. That is a promise worth printing, because an agent who believes a second
 *   layout costs another run will never try one.
 */
import { useEffect, useState } from "react";

import { getFormats, type LayoutOption } from "./api";
import { themeVar as colors } from "../design-tokens";
import { blockStyle } from "../ui/Block";

export type LayoutPickerProps = {
  value: string | null;
  onChange: (code: string) => void;
  /** Rendered under the options. Kept as a prop so the copy lives with the screen that uses it. */
  note?: string;
  /** Reports the loaded registry up, so the screen does not fetch it a SECOND time.
   *  Two independent fetches could disagree: if the screen's failed while this one succeeded, the
   *  agent could pick a PDF-only layout and still be offered a Word button the server refuses --
   *  precisely what this component promises cannot happen. One fetch, one answer. */
  onOptionsLoaded?: (options: LayoutOption[]) => void;
  /** LAYOUTS THIS PARTICULAR BRIEF CANNOT PRODUCE — code -> the reason, shown in place.
   *
   * The registry answers which layouts EXIST. This answers which of them THIS job can make, and
   * that second question was missing. A brief built before the structured figure block cannot be
   * rendered as Folio, Panorama or Album; the server refuses with a 400 and an actionable
   * sentence. Until this prop existed the picker offered all four regardless, and the agent found
   * out by landing on a raw JSON error page — the exact dead end this component exists to remove.
   *
   * The reason is passed IN rather than composed here, because the screen holds the payload and
   * the wording should stay close to the sentence the server itself would have answered with. */
  unavailable?: Record<string, string>;
  disabled?: boolean;
};

export function LayoutPicker({
  value, onChange, note, disabled, onOptionsLoaded, unavailable,
}: LayoutPickerProps) {
  const [options, setOptions] = useState<LayoutOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getFormats()
      .then((r) => {
        if (!live) return;
        setOptions(r.formats);
        onOptionsLoaded?.(r.formats);
        // SHOW WHAT WILL ACTUALLY HAPPEN. With nothing selected, four empty radios sat above a
        // live "Download PDF" button and the agent had no way to know which document they would
        // get. This is not a default being invented in the client: the server states which layout
        // a caller that names none receives, and this reflects that answer back so the screen and
        // the download agree. The registry still refuses `resolve(None)`.
        if (value === null && r.legacy) onChange(r.legacy);
      })
      .catch((e: unknown) => {
        // Say what happened. A picker that silently renders nothing looks like a product with one
        // layout, which is the state this whole control exists to end.
        if (live) setError(e instanceof Error ? e.message : "the layout list could not be loaded");
      });
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return (
      <div role="alert" style={{ fontSize: 13, color: colors.textSecondary }}>
        The report formats could not be loaded, so this brief will download in the format it was
        built in. {error}
      </div>
    );
  }

  if (!options) {
    return (
      <div style={{ fontSize: 13, color: colors.textSecondary }}>Loading report formats…</div>
    );
  }

  return (
    <fieldset
      style={{ border: "none", padding: 0, margin: 0 }}
      disabled={disabled}
    >
      {/* THE PROTOTYPE'S TYPE PICKER (2026-08-30). `.lbl` / `.typeGrid` / `.typeOpt` / `.typeOpt.on`
          / `.typeOpt .t` / `.typeOpt .d` all ship in `mock-design.css` and rendered nowhere. Two
          things change beyond the styling, and both were defects rather than taste:

          - The four options stacked as full-width cards. `.typeGrid` is
            `repeat(auto-fit, minmax(min(100%,150px), 1fr))`, so they lay out across the measure —
            four choices you can compare at a glance instead of scroll past.
          - Selection was drawn in `colors.verified`, the GREEN this product uses to mean "verified
            against a named source". Picking a page layout is not an evidence claim. `.typeOpt.on`
            is the navy accent with a 3px inset rule, which is what the prototype does and what the
            rest of the app now does. */}
      <legend className="lbl">Report format</legend>

      <div className="typeGrid">
        {options.map((o) => {
          const selected = value === o.code;
          // WHY THIS BRIEF CANNOT MAKE THIS LAYOUT, or "". Not a style choice: the option stays
          // VISIBLE and states its reason rather than disappearing, because a format that vanishes
          // reads as a product that does not have it, and this one does — for the next brief.
          const blocked = (unavailable && unavailable[o.code]) || "";
          const off = !!blocked || !!disabled;
          return (
            <label
              key={o.code}
              className={selected ? "typeOpt on" : "typeOpt"}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                ...(blocked ? { background: "#FAFBFC" } : null),
                cursor: off ? "default" : "pointer",
              }}
            >
              <input
                type="radio"
                name="vb-layout"
                value={o.code}
                checked={selected}
                disabled={off}
                onChange={() => onChange(o.code)}
                style={{ marginTop: 3, accentColor: colors.accent }}
              />
              <span>
                <span
                  className="t"
                  style={
                    // Dimmed, never hidden, and never the only signal — the reason below says it
                    // in words, so this does not depend on seeing a colour difference.
                    blocked ? { color: colors.textSecondary } : undefined
                  }
                >
                  {o.name}
                </span>
                {o.landscape && (
                  <span style={{ fontSize: 12, color: colors.textSecondary, marginLeft: 8 }}>
                    landscape
                  </span>
                )}
                {/* EVERY option carries a file-type badge, including the one that can do both.
                    With a badge only on the restricted layouts, Focus was the single unmarked
                    option -- and `formats.py` is explicit that none of the four is the unmarked
                    member. An absent badge reads as "the normal one". */}
                <span style={{ fontSize: 12, color: colors.textSecondary, marginLeft: 8 }}>
                  {o.docx ? "PDF and Word" : "PDF only"}
                </span>
                <span className="d">{o.describe}</span>
                {/* THE REASON, WHERE THE AGENT IS ALREADY LOOKING. This is the sentence the server
                    would have answered with after the click; saying it here means there is no
                    click to recover from. */}
                {blocked && (
                  <span
                    style={{
                      display: "block",
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: colors.textPrimary,
                      marginTop: 6,
                      paddingTop: 6,
                      borderTop: `1px solid ${colors.border}`,
                    }}
                  >
                    {blocked}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {note && (
        <p style={{ fontSize: 13, color: colors.textSecondary, margin: "10px 0 0", ...blockStyle("copy") }}>{note}</p>
      )}
    </fieldset>
  );
}

export default LayoutPicker;
